import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Upload, X, ImagePlus, Check, AlertCircle, Cloud, Video, Film, Tag, Plus, Pencil, Trash2, Palette, Globe, Lock } from 'lucide-react';
import ConfirmModal from '../ConfirmModal';

const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const PRESET_COLORS = ['#e57373','#f06292','#ba68c8','#9575cd','#7986cb','#64b5f6','#4fc3f7','#4dd0e1','#4db6ac','#81c784','#aed581','#dce775','#ffd54f','#ffb74d','#ff8a65','#a1887f'];

const DashboardUpload = () => {
  const [buckets, setBuckets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [defaultCategory, setDefaultCategory] = useState(() => localStorage.getItem('upload-default-category') || '');
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [defaultVisibility, setDefaultVisibility] = useState(() => localStorage.getItem('upload-default-visibility') || 'public');

  // Inline create category
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState(PRESET_COLORS[0]);
  const [showNewCat, setShowNewCat] = useState(false);
  const [creatingCat, setCreatingCat] = useState(false);
  const newCatInputRef = useRef(null);

  // Full category management
  const [catFormOpen, setCatFormOpen] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [catFormName, setCatFormName] = useState('');
  const [catFormSlug, setCatFormSlug] = useState('');
  const [catFormColor, setCatFormColor] = useState(PRESET_COLORS[0]);
  const [confirmDeleteCat, setConfirmDeleteCat] = useState(null);
  const [showCatManager, setShowCatManager] = useState(false);

  const fetchCategories = useCallback(() => {
    axios.get('/api/categories').then((res) => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get('/api/buckets'),
      axios.get('/api/categories'),
    ]).then(([bRes, cRes]) => {
      setBuckets(bRes.data);
      setCategories(cRes.data);
      if (bRes.data.length > 0) setSelectedBucket(bRes.data[0].id);
    }).catch(() => {});
  }, []);

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) { toast.error('Category name required'); return; }
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error('Category already exists'); return;
    }
    setCreatingCat(true);
    try {
      const res = await axios.post('/api/categories', { name, color: newCatColor });
      const newCat = res.data;
      setCategories((prev) => [...prev, newCat]);
      setDefaultCategory(newCat.id);
      localStorage.setItem('upload-default-category', newCat.id);
      // Apply to pending queue items
      setQueue((prev) => prev.map((item) => item.status === 'pending' ? { ...item, categoryId: newCat.id } : item));
      setNewCatName('');
      setShowNewCat(false);
      toast.success(`Category "${name}" created`);
    } catch {
      toast.error('Failed to create category');
    }
    setCreatingCat(false);
  };

  useEffect(() => {
    if (showNewCat && newCatInputRef.current) newCatInputRef.current.focus();
  }, [showNewCat]);

  // ─── Full Category CRUD ───
  const openCatForm = (cat) => {
    if (cat) {
      setEditCat(cat);
      setCatFormName(cat.name);
      setCatFormSlug(cat.slug || '');
      setCatFormColor(cat.color);
    } else {
      setEditCat(null);
      setCatFormName('');
      setCatFormSlug('');
      setCatFormColor(PRESET_COLORS[0]);
    }
    setCatFormOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!catFormName.trim()) { toast.error('Name is required'); return; }
    const slug = catFormSlug.trim() || catFormName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    try {
      if (editCat) {
        const res = await axios.put(`/api/categories/${editCat.id}`, { name: catFormName.trim(), slug, color: catFormColor });
        setCategories((prev) => prev.map((c) => c.id === editCat.id ? res.data : c));
        toast.success('Category updated');
      } else {
        const res = await axios.post('/api/categories', { name: catFormName.trim(), slug, color: catFormColor });
        setCategories((prev) => [...prev, res.data]);
        toast.success('Category created');
      }
      setCatFormOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const handleDeleteCategory = async (cat) => {
    setConfirmDeleteCat(null);
    try {
      await axios.delete(`/api/categories/${cat.id}`);
      toast.success('Category deleted');
      setCategories((prev) => prev.filter((c) => c.id !== cat.id));
      // Reset default category if deleted one was selected
      if (defaultCategory === cat.id) {
        setDefaultCategory('');
        localStorage.removeItem('upload-default-category');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const isVideo = (file) => file.type.startsWith('video/');

  const addFiles = useCallback((files) => {
    const valid = Array.from(files).filter((f) => {
      const ok = f.type.startsWith('image/') || f.type.startsWith('video/');
      if (!ok) { toast.error(`${f.name}: only images and videos allowed`); return false; }
      const max = f.type.startsWith('video/') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (f.size > max) { toast.error(`${f.name}: max ${formatBytes(max)}`); return false; }
      return true;
    });
    setQueue((prev) => [...prev, ...valid.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      status: 'pending',
      type: isVideo(f) ? 'video' : 'image',
      categoryId: defaultCategory,
      visibility: defaultVisibility,
    }))]);
  }, [defaultCategory, defaultVisibility]);

  const handleFileSelect = (e) => { addFiles(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; };
  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };

  useEffect(() => {
    const h = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (const item of items) { if (item.type.startsWith('image/')) { const f = item.getAsFile(); if (f) files.push(f); } }
      if (files.length > 0) addFiles(files);
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  }, [addFiles]);

  const updateFileCategory = (idx, catId) => {
    setQueue((prev) => prev.map((item, i) => i === idx ? { ...item, categoryId: catId } : item));
  };

  const updateFileVisibility = (idx, vis) => {
    setQueue((prev) => prev.map((item, i) => i === idx ? { ...item, visibility: vis } : item));
  };

  const applyCategoryToAll = (catId) => {
    setQueue((prev) => prev.map((item) => item.status === 'pending' ? { ...item, categoryId: catId } : item));
    setDefaultCategory(catId);
    localStorage.setItem('upload-default-category', catId);
  };

  const applyVisibilityToAll = (vis) => {
    setQueue((prev) => prev.map((item) => item.status === 'pending' ? { ...item, visibility: vis } : item));
    setDefaultVisibility(vis);
    localStorage.setItem('upload-default-visibility', vis);
  };

  const removeFromQueue = (idx) => {
    setQueue((prev) => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };

  const clearQueue = () => { queue.forEach((i) => URL.revokeObjectURL(i.preview)); setQueue([]); };

  const uploadAll = async () => {
    if (queue.length === 0 || uploading) return;
    if (!selectedBucket && buckets.length > 0) { toast.error('Select a bucket first'); return; }
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].status === 'done') continue;
      setCurrentIdx(i); setProgress(0);
      try {
        const fd = new FormData();
        fd.append('photo', queue[i].file);
        if (selectedBucket) fd.append('bucketId', selectedBucket);
        if (queue[i].categoryId) fd.append('categoryId', queue[i].categoryId);
        fd.append('visibility', queue[i].visibility || 'public');
        await axios.post('/api/photos/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
        });
        setQueue((prev) => prev.map((item, j) => j === i ? { ...item, status: 'done' } : item));
        ok++;
      } catch {
        setQueue((prev) => prev.map((item, j) => j === i ? { ...item, status: 'error' } : item));
      }
    }
    setUploading(false); setCurrentIdx(-1); setProgress(0);
    if (ok > 0) toast.success(`${ok} file${ok > 1 ? 's' : ''} uploaded successfully`);
    setTimeout(() => setQueue((prev) => prev.filter((i) => i.status !== 'done')), 2000);
  };

  useEffect(() => () => { queue.forEach((i) => URL.revokeObjectURL(i.preview)); }, []);

  const pending = queue.filter((i) => i.status === 'pending').length;
  const done = queue.filter((i) => i.status === 'done').length;
  const errors = queue.filter((i) => i.status === 'error').length;

  const getCategoryById = (id) => categories.find((c) => c.id === id);

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="mb-5">
        <h2 className="font-heading font-bold text-lg text-foreground">Upload Media</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Drag, drop, or paste photos & videos</p>
      </div>

      {/* Selectors row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {/* Bucket selector */}
        {buckets.length > 0 ? (
          <div>
            <label className="label">Storage bucket</label>
            <select value={selectedBucket} onChange={(e) => setSelectedBucket(e.target.value)} className="select">
              {buckets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-warning/10 border border-warning/20 text-warning-foreground px-3 py-2 rounded-xl text-xs font-medium">
            <Cloud size={14} className="shrink-0" />
            <span>No buckets.</span>
            <a href="/dashboard/settings" className="ml-auto font-semibold text-amber-700 hover:underline">Add</a>
          </div>
        )}

        {/* Default category selector + create */}
        <div>
          <label className="label">Default category</label>
          <div className="flex gap-2">
            <select value={defaultCategory} onChange={(e) => applyCategoryToAll(e.target.value)} className="select flex-1">
              <option value="">Tanpa kategori</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              onClick={() => setShowNewCat(!showNewCat)}
              className="shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-border/60 bg-surface hover:bg-primary/5 hover:border-primary/30 text-muted-foreground hover:text-primary transition-all cursor-pointer"
              title="Create new category"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Default visibility selector */}
        <div>
          <label className="label">Visibilitas Default</label>
          <div className="flex gap-1.5">
            <button
              onClick={() => applyVisibilityToAll('public')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                defaultVisibility === 'public'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm'
                  : 'bg-card border border-border/60 text-muted-foreground hover:border-emerald-200 hover:text-emerald-600'
              }`}
            >
              <Globe size={13} /> Public
            </button>
            <button
              onClick={() => applyVisibilityToAll('private')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                defaultVisibility === 'private'
                  ? 'bg-amber-50 text-amber-700 border border-amber-200 shadow-sm'
                  : 'bg-card border border-border/60 text-muted-foreground hover:border-amber-200 hover:text-amber-600'
              }`}
            >
              <Lock size={13} /> Private
            </button>
          </div>
        </div>
      </div>

      {/* Inline create category */}
      {showNewCat && (
        <div className="mb-5 bg-surface-container-low rounded-2xl p-4 border border-border/40 animate-fade-in">
          <p className="text-xs font-bold text-foreground mb-3">New category</p>
          <div className="flex items-center gap-2 mb-3">
            <input
              ref={newCatInputRef}
              type="text"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createCategory(); if (e.key === 'Escape') setShowNewCat(false); }}
              placeholder="e.g. PPH, PPK, Liburan..."
              className="flex-1 px-3 py-2 rounded-xl border border-border/60 bg-surface text-sm font-medium text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary/30 transition-all"
              maxLength={30}
            />
            <button
              onClick={createCategory}
              disabled={creatingCat || !newCatName.trim()}
              className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold shadow-bubble hover:scale-95 active:scale-90 transition-all cursor-pointer ${
                newCatName.trim()
                  ? 'bg-primary text-on-primary hover:shadow-md'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {creatingCat ? '...' : 'Create'}
            </button>
          </div>
          {/* Color picker */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-muted-foreground font-bold mr-1">Color:</span>
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setNewCatColor(c)}
                className={`w-5 h-5 rounded-full transition-all cursor-pointer ${newCatColor === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-110'}`}
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Drop zone */}
      <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-3xl p-10 text-center transition-all duration-300 cursor-pointer group ${
          dragActive ? 'border-primary bg-primary/5 scale-[1.01] shadow-bubble' : 'border-border hover:border-primary/30 hover:bg-muted/30'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
        <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={handleFileSelect} className="hidden" aria-label="Select files" />
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 ${
          dragActive ? 'bg-primary/15 scale-110' : 'bg-muted group-hover:bg-primary/5'
        }`}>
          <ImagePlus size={28} className={`transition-colors duration-300 ${dragActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'}`} />
        </div>
        <p className="text-sm font-semibold text-foreground mb-1">
          {dragActive ? 'Drop your files here' : 'Drag & drop atau klik untuk memilih file'}
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (!uploading) fileInputRef.current?.click(); }}
          className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-container text-on-primary-container text-xs font-bold shadow-bubble hover:scale-95 active:scale-90 transition-all cursor-pointer"
        >
          <ImagePlus size={14} /> Pilih File
        </button>
        <p className="text-xs text-foreground/70 mt-3">
          Images: JPG, PNG, GIF, WebP (max 500MB) · Videos: MP4, WebM, MOV (max 5GB)
        </p>
        <p className="text-[11px] text-foreground/50 mt-1">Paste dari clipboard juga bisa untuk gambar</p>
      </div>

      {/* Queue */}
      {queue.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-foreground">{queue.length} file{queue.length !== 1 ? 's' : ''}</p>
              {done > 0 && <span className="badge-success"><Check size={10} /> {done} done</span>}
              {errors > 0 && <span className="badge-destructive"><AlertCircle size={10} /> {errors} failed</span>}
            </div>
            {!uploading && (
              <button onClick={clearQueue} className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer font-medium">Clear all</button>
            )}
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin pr-1">
            {queue.map((item, idx) => {
              const cat = getCategoryById(item.categoryId);
              return (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-2xl border transition-all duration-200 ${
                  item.status === 'done' ? 'bg-success/5 border-success/20' :
                  item.status === 'error' ? 'bg-destructive/5 border-destructive/20' :
                  currentIdx === idx && uploading ? 'bg-primary/5 border-primary/20 shadow-bubble' :
                  'bg-card border-border/50 hover:border-primary/15'
                }`}>
                  {/* Preview */}
                  <div className="w-10 h-10 rounded-xl overflow-hidden bg-muted shrink-0 relative">
                    {item.type === 'video' ? (
                      <>
                        <video src={item.preview} className="w-full h-full object-cover" muted />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Film size={14} className="text-white" />
                        </div>
                      </>
                    ) : (
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{item.file.name}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-[11px] text-muted-foreground font-mono">{formatBytes(item.file.size)}</p>
                      {item.type === 'video' && <span className="text-[10px] text-purple-600 font-semibold">VIDEO</span>}
                    </div>
                    {currentIdx === idx && uploading && (
                      <div className="mt-2 progress-bar"><div className="progress-bar-fill" style={{ width: `${progress}%` }} /></div>
                    )}
                  </div>
                  {/* Per-file category selector */}
                  {item.status === 'pending' && !uploading && categories.length > 0 && (
                    <div className="shrink-0">
                      <select
                        value={item.categoryId || ''}
                        onChange={(e) => updateFileCategory(idx, e.target.value || null)}
                        className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-border/60 bg-surface text-foreground cursor-pointer hover:border-primary/30 transition-colors max-w-[120px]"
                        aria-label={`Category for ${item.file.name}`}
                      >
                        <option value="">Tanpa kategori</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                  {/* Per-file visibility selector */}
                  {item.status === 'pending' && !uploading && (
                    <div className="shrink-0">
                      <button
                        onClick={() => updateFileVisibility(idx, item.visibility === 'public' ? 'private' : 'public')}
                        className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg border transition-all cursor-pointer ${
                          item.visibility === 'private'
                            ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                        }`}
                        aria-label={`Visibility for ${item.file.name}`}
                      >
                        {item.visibility === 'private' ? <Lock size={10} /> : <Globe size={10} />}
                        {item.visibility === 'private' ? 'Private' : 'Public'}
                      </button>
                    </div>
                  )}
                  {item.status === 'pending' && !uploading && cat && (
                    <div className="shrink-0 hidden sm:block">
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border"
                        style={{ borderColor: cat.color + '40', backgroundColor: cat.color + '15', color: cat.color }}
                      >
                        <Tag size={9} />
                        {cat.name}
                      </span>
                    </div>
                  )}
                  <div className="shrink-0">
                    {item.status === 'done' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check size={12} /> Done</span>
                    ) : item.status === 'error' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><AlertCircle size={12} /> Failed</span>
                    ) : !uploading ? (
                      <button onClick={() => removeFromQueue(idx)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer" aria-label="Remove">
                        <X size={14} />
                      </button>
                    ) : currentIdx === idx ? (
                      <span className="text-xs font-mono text-muted-foreground">{progress}%</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={uploadAll} disabled={uploading || pending === 0} className="w-full mt-4 btn-primary py-3 text-sm">
            {uploading ? (
              <><span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" /> Uploading...</>
            ) : (
              <><Upload size={16} /> Upload {pending} file{pending !== 1 ? 's' : ''}</>
            )}
          </button>
        </div>
      )}
      {/* ═══ CATEGORY MANAGER ═══ */}
      <div className="mt-8">
        <button
          onClick={() => setShowCatManager(true)}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors cursor-pointer mb-3"
        >
          <Tag size={15} />
          Kelola Kategori
          <span className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded-md">{categories.length}</span>
        </button>
      </div>

      {/* Category Manager Modal */}
      {showCatManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => setShowCatManager(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col animate-scale-in" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 shrink-0">
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-primary" />
                <h3 className="font-heading font-bold text-sm text-foreground">Kelola Kategori</h3>
                <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded-md text-muted-foreground">{categories.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openCatForm(null)} className="btn-primary btn-sm">
                  <Plus size={14} /> Add
                </button>
                <button onClick={() => setShowCatManager(false)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer">
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {categories.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Tag size={20} className="text-primary" />
                  </div>
                  <p className="text-sm font-semibold text-foreground mb-1">Belum ada kategori</p>
                  <p className="text-xs text-muted-foreground mb-3">Buat kategori untuk mengorganisasi upload.</p>
                  <button onClick={() => openCatForm(null)} className="text-xs font-medium text-primary hover:text-primary/80 cursor-pointer">
                    Buat kategori pertama
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {categories.map((c) => (
                    <div key={c.id} className="px-4 py-3 flex items-center gap-3 group hover:bg-muted/50 rounded-xl transition-colors">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: c.color + '15' }}>
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground">{c.name}</h4>
                        <p className="text-[11px] text-muted-foreground font-mono">/{c.slug}</p>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button onClick={() => openCatForm(c)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer" aria-label="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setConfirmDeleteCat(c)} className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-lg transition-colors cursor-pointer" aria-label="Delete">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Category form modal */}
      {catFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={() => setCatFormOpen(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-5 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-sm text-foreground">{editCat ? 'Edit Category' : 'New Category'}</h3>
              <button onClick={() => setCatFormOpen(false)} className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Name</label>
                <input type="text" value={catFormName} onChange={(e) => setCatFormName(e.target.value)}
                  placeholder="e.g. PPH, PPK, Liburan..."
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all" autoFocus />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Slug <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input type="text" value={catFormSlug} onChange={(e) => setCatFormSlug(e.target.value)}
                  placeholder="auto-generated from name"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all" />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Color</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button key={c} onClick={() => setCatFormColor(c)}
                      className={`w-6 h-6 rounded-full border-2 transition-all duration-200 cursor-pointer ${catFormColor === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                  <div className="relative w-6 h-6">
                    <Palette size={12} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white pointer-events-none z-10" />
                    <input type="color" value={catFormColor} onChange={(e) => setCatFormColor(e.target.value)}
                      className="w-6 h-6 rounded-full cursor-pointer border-0 p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-xl">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: catFormColor }} />
                <span className="text-sm font-medium text-foreground">{catFormName || 'Category name'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <button onClick={() => setCatFormOpen(false)} className="btn-outline btn-sm flex-1">Cancel</button>
              <button onClick={handleSaveCategory} className="btn-primary btn-sm flex-1">{editCat ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDeleteCat}
        title="Delete category?"
        message={`"${confirmDeleteCat?.name}" will be removed. Media in this category will not be deleted.`}
        confirmLabel="Delete"
        onConfirm={() => confirmDeleteCat && handleDeleteCategory(confirmDeleteCat)}
        onCancel={() => setConfirmDeleteCat(null)}
      />
    </div>
  );
};

export default DashboardUpload;
